import type { BankTransactionDto } from "Infrastructure/Api/Api";
import { formatCurrency } from "Infrastructure/Utilities/currencyFormat";
import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import { defaultTableConfig } from "Infrastructure/Utilities/defaultTableConfig";
import { useUpdatePaymentMutation } from "Payment/Api/Mutation/useUpdatePaymentMutation";
import { usePaymentsQuery } from "Payment/Api/Query/usePaymentsQuery";
import { unassignedPaymentsAtom } from "Payment/Atom/paymentAtom";
import {
	UpdatePaymentModal,
	type UpdatePaymentModalFormData,
} from "Payment/Components/Modals/UpdatePaymentModal";
import { usePaymentFilters } from "Payment/Hooks/usePaymentFilters";
import Edit from "@mui/icons-material/Edit";
import { IconButton, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { createMRTColumnHelper } from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";

type BankTransactionTableRow = BankTransactionDto;

export const useUnassignedPaymentsTable = () => {
	const columnHelper = createMRTColumnHelper<BankTransactionTableRow>();
	const filters = usePaymentFilters();
	const query = usePaymentsQuery(filters);
	const { formatDate } = useDateFormat();
	const { mutate: updatePayment } = useUpdatePaymentMutation();
	const [_, setUnassignedPaymentsNum] = useAtom(unassignedPaymentsAtom);

	// Modal state
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedPayment, setSelectedPayment] =
		useState<BankTransactionDto | null>(null);

	// Modal handlers
	const handleOpenModal = useCallback((payment: BankTransactionDto) => {
		setSelectedPayment(payment);
		setIsModalOpen(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		setSelectedPayment(null);
	}, []);

	const handleUpdatePayment = useCallback(
		(
			data: UpdatePaymentModalFormData,
			payment: Partial<BankTransactionDto>,
		) => {
			if (!payment.id) {
				return;
			}

			const orderIdNumber = data.orderId
				? Number.parseInt(data.orderId, 10)
				: null;

			updatePayment(
				{
					id: payment.id,
					orderId: orderIdNumber,
					isRenovation: data.isRenovation,
				},
				{
					onSuccess: () => {
						handleCloseModal();
					},
				},
			);
		},
		[updatePayment, handleCloseModal],
	);

	// Filter out only "unassigned" payments (those without orderId)
	const tableData = useMemo(() => {
		return (query.data?.data ?? []).filter((payment) => !payment.orderId);
	}, [query.data?.data]);

	useEffect(() => {
		setUnassignedPaymentsNum(tableData.length);
	}, [tableData, setUnassignedPaymentsNum]);

	const columns = [
		columnHelper.accessor((row) => row.createdAt, {
			id: "createdAt",
			header: "Date Created",
			enableColumnActions: false,
			maxSize: 120,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{formatDate(row.original.createdAt)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.amount, {
			id: "amount",
			header: "Amount",
			enableColumnActions: false,
			maxSize: 120,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{formatCurrency(
						row.original.currency,
						row.original.currency,
						row.original.amount,
					)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.reference, {
			id: "reference",
			header: "Reference",
			enableColumnActions: false,
			maxSize: 150,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">{row.original.reference}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.updatedAt, {
			id: "updatedAt",
			header: "Latest sync",
			enableColumnActions: false,
			maxSize: 120,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{formatDate(row.original.updatedAt)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.bankDetails.bankAccount, {
			id: "bankAccount",
			header: "Bank Account",
			enableColumnActions: false,
			maxSize: 120,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.bankDetails.iban.trim().length > 0
						? row.original.bankDetails.iban
						: row.original.bankDetails.bankAccount}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.note, {
			id: "note",
			header: "Note",
			enableColumnActions: false,
			maxSize: 250,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">{row.original.note}</Typography>
			),
		}),
		{
			id: "actions",
			header: "Actions",
			enableColumnActions: false,
			enableSorting: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }) => (
				<IconButton
					size="small"
					onClick={() => handleOpenModal(row.original)}
					sx={{ background: "none" }}
				>
					<Edit fontSize="small" />
				</IconButton>
			),
		},
	];

	const tableOptions = {
		...defaultTableConfig,
		columns,
		data: tableData,
		state: {
			showSkeletons: query.isLoading,
			isLoading: query.isLoading,
		},
		muiTableBodyRowProps: ({ row }) => ({
			onDoubleClick: () => {
				handleOpenModal(row.original);
			},
		}),
	};

	return {
		tableOptions,
		modal: (
			<UpdatePaymentModal
				isOpen={isModalOpen}
				payment={selectedPayment}
				onClose={handleCloseModal}
				onSubmit={handleUpdatePayment}
			/>
		),
	};
};
